import {
  FilledDynamicValueTable,
  VegaDataFrame,
  VegaDisplayOverrides,
  VegaLiteSpec,
  VegaMetadata,
  getLayerNameByVariableName,
  getVegaLiteSpecWithDisplayConfig,
  getWithDatasetReferences,
} from "@hex/common";
import { useMemo } from "react";
import { Result } from "runtypes";

const getRowsForTable = (
  table: Result<FilledDynamicValueTable>,
): Record<string, unknown>[] => {
  if (!table.success) {
    return [];
  }

  const column = table.value;
  const columnEntries: [colName: string, content: unknown[]][] = Object.entries(
    column.contents,
  ).map(([colId, content]) => [
    column.headers.find((head) => head.id === colId)?.columnName?.toString() ??
      colId,
    content,
  ]);

  const rows: Record<string, unknown>[] = [];
  for (const rowIndex of Array(column.rowCount).keys()) {
    rows.push(
      Object.fromEntries(
        columnEntries.map<[string, unknown]>(([colName, content]) => [
          colName,
          content[rowIndex],
        ]),
      ),
    );
  }

  return rows;
};

export type Vega = {
  spec: VegaLiteSpec;
  layerNameByVariableName: Record<string, string>;
};

export const useHydratedVegaLiteSpec = ({
  dataFrames,
  displayOverrides,
  metadata,
  vegaLiteSpec: vegaLiteSpec_,
}: {
  vegaLiteSpec: VegaLiteSpec;
  metadata: VegaMetadata | null;
  dataFrames: VegaDataFrame[];
  displayOverrides?: VegaDisplayOverrides;
}): VegaLiteSpec => {
  const layerNameByVariableName = useMemo(
    () => getLayerNameByVariableName(metadata),
    [metadata],
  );

  const rowsByVariableName = useMemo(
    () =>
      dataFrames.reduce<Record<string, Record<string, unknown>[]>>(
        (acc, { name, table }) => {
          if (name != null && acc[name] == null) {
            return {
              ...acc,
              [name]: getRowsForTable(table),
            };
          }
          return acc;
        },
        {},
      ),
    [dataFrames],
  );

  const vegaLiteSpec: VegaLiteSpec = useMemo(
    () =>
      getWithDatasetReferences({
        vegaLiteSpec: vegaLiteSpec_,
        metadata,
        layerNameByVariableName,
      }),
    [vegaLiteSpec_, metadata, layerNameByVariableName],
  );

  return useMemo(
    () => ({
      ...getVegaLiteSpecWithDisplayConfig({
        vegaLiteSpec,
        displayOverrides,
      }),
      datasets: Object.entries(rowsByVariableName).reduce(
        (acc, [name, rows]) => {
          const layerName = layerNameByVariableName[name];
          if (layerName == null) {
            return acc;
          }
          return {
            ...acc,
            [layerName]: rows,
          };
        },
        {},
      ),
    }),
    [
      vegaLiteSpec,
      displayOverrides,
      rowsByVariableName,
      layerNameByVariableName,
    ],
  );
};
